import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { useFetchWrapper } from '../../../_helpers/fetchWrapper.js';
import { signOut } from '../../../_helpers/signOut.js';
import { resolveMediaUrl } from '../../../hooks/useStreamBootstrap.js';
import config from '../../../config.js';
import { focusFirstIn } from './headerMenuFocus.js';

/**
 * The account menu under the header avatar (mockup 29, ENG-723), from
 * `GET /me` (ENG-724): the listener's name and engagement tier, Profile,
 * History, Achievements and Shop, YOUR BANDS (each band's page), Register as a
 * performer (shown to band members too, as in the frame) and Sign out. Esc
 * and a click outside close it (`onClose`).
 */
const AccountMenu = ({ onClose, inline = false, id }) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const fetchWrapperRef = useRef(useFetchWrapper());
  const ref = useRef(null);
  const [me, setMe] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/me`)
      .then((answer) => { if (!cancelled) setMe(answer); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);

  // Focus moves into the menu as it opens (ENG-840).
  useEffect(() => { focusFirstIn(ref.current); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose('escape'); };
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.('[data-account-trigger]')) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  const bands = Array.isArray(me?.bands) ? me.bands : [];
  const avatar = resolveMediaUrl(me?.avatarUrl);

  return (
    <div
      className={`header-menu account-menu${inline ? ' header-menu--inline' : ''}`}
      ref={ref}
      id={id}
      role={inline ? 'region' : 'dialog'}
      aria-label={t('stream.accountMenu.label')}
      data-testid="account-menu"
    >
      <div className="account-menu-head">
        <span className="account-menu-avatar" aria-hidden="true">
          {avatar ? <img src={avatar} alt="" /> : (me?.initials || '')}
        </span>
        <div>
          <p className="account-menu-name">{me?.displayName ?? (failed ? '' : t('common.status.loadingEllipsis'))}</p>
          {me?.engagementTier && (
            <span className="account-menu-tier" data-testid="account-menu-tier">
              {t('stream.accountMenu.tier', { tier: me.engagementTier })}
            </span>
          )}
        </div>
      </div>
      <nav className="account-menu-links" aria-label={t('stream.accountMenu.label')}>
        <Link to="/profile" onClick={onClose}>{t('stream.accountMenu.profile')}</Link>
        <Link to="/history" onClick={onClose}>{t('stream.accountMenu.history')}</Link>
        <Link to="/achievements" onClick={onClose}>{t('stream.accountMenu.achievements')}</Link>
        {/* The header's top links hide at 900px and below; Shop stays reachable here (ENG-864). */}
        <Link to="/shop" onClick={onClose} data-testid="account-menu-shop">{t('stream.accountMenu.shop')}</Link>
      </nav>
      {bands.length > 0 && (
        <div className="account-menu-bands">
          <p className="account-menu-section">{t('stream.accountMenu.yourBands')}</p>
          <ul>
            {bands.map((band) => (
              <li key={band.id}>
                <Link to={`/performers/${band.id}`} onClick={onClose} data-testid={`account-menu-band-${band.id}`}>
                  {band.name}
                  <FaChevronRight className="rtl-mirror" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="account-menu-links account-menu-foot">
        <Link to="/register-band" onClick={onClose}>{t('stream.accountMenu.registerPerformer')}</Link>
        <button type="button" onClick={() => signOut(fetchWrapperRef.current, { navigate })} data-testid="account-menu-signout">
          {t('stream.accountMenu.signOut')}
        </button>
      </div>
    </div>
  );
};

export default AccountMenu;
